import { createClient } from '@connectrpc/connect';
import { RevitService } from '../../shared/api/grpc/gen/revit_service_pb';
import { getGrpcTransport } from '../../shared/api/grpc/transport';
import { webLog } from '../../shared/api/grpc/webLogger';
import type {
  ExportedModelPlan,
  ModelChunkData,
  ModelTransport,
} from './modelTransport';

/**
 * gRPC adapter for {@link ModelTransport}. Unary calls only (GetRecorderExport +
 * ReadRecorderModelChunk) — both safe under WebView2, unlike streaming. int64
 * wire fields arrive as `bigint`; converted to `number` at this boundary.
 */
export function createGrpcModelTransport(): ModelTransport {
  function client() {
    const transport = getGrpcTransport();
    if (!transport) {
      throw new Error('gRPC transport is unavailable');
    }
    return createClient(RevitService, transport);
  }

  return {
    async exportModel(includeModel: boolean): Promise<ExportedModelPlan> {
      const res = await client().getRecorderExport({ includeModel });
      if (!res.isSuccess) {
        throw new Error(res.error || 'GetRecorderExport failed');
      }
      const model = res.model;
      const mode = model?.mode === 'copy' ? 'copy' : 'reference';
      const reference = {
        title: model?.reference?.title ?? '',
        pathName: model?.reference?.pathName || undefined,
        documentGuid: model?.reference?.documentGuid || undefined,
        revitVersion: model?.reference?.revitVersion || undefined,
      };

      const plan: ExportedModelPlan = {
        mode,
        warning: model?.warning || undefined,
        stagingToken: res.stagingToken ?? '',
        reference,
        linkedModels: (model?.linkedModels ?? []).map((l) => ({
          name: l.name,
          archiveRef: l.archiveRef,
          sizeBytes: Number(l.sizeBytes ?? 0n),
          isLoaded: l.isLoaded,
        })),
      };

      if (mode === 'copy' && model?.archiveRef) {
        plan.host = { archiveRef: model.archiveRef, sizeBytes: Number(model.sizeBytes ?? 0n) };
      }
      return plan;
    },

    async readChunk(
      stagingToken: string,
      archiveRef: string,
      offset: number,
      maxBytes: number,
    ): Promise<ModelChunkData> {
      const res = await client().readRecorderModelChunk({
        stagingToken,
        archiveRef,
        offset: BigInt(offset),
        maxBytes,
      });
      if (!res.isSuccess) {
        webLog.warn('grpcModelTransport', `readChunk ${archiveRef}@${offset}: ${res.error}`);
        throw new Error(res.error || 'ReadRecorderModelChunk failed');
      }
      return {
        data: res.data ?? new Uint8Array(0),
        eof: res.eof,
        totalSize: Number(res.totalSize ?? 0n),
      };
    },
  };
}
